import React, { useState } from "react";
import { PanelRightClose, User, LogOut, Settings, Wallet, Link as LinkIcon, Unlink } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { useWallet } from "../../contexts/WalletContext";
import { useBrand } from "../../contexts/BrandContext";
import { Button } from "../ui/button";
import { ConsentLevelIndicator, ConsentEscalationButton } from "../Wallet";

import Dropdown from "./Dropdown";

const Header = ({
  onPanelClick = () => {},
  onUserClick = () => {},
  isHistoryOpen = false,
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isConnectingWallet, setIsConnectingWallet] = useState(false);
  const { signOut, user, signIn, isLoading } = useAuth();
  const { isConnected: walletConnected, walletProfile, connectWallet, disconnectWallet } = useWallet();
  const { brand } = useBrand();
  const navigate = useNavigate();

  const handleUserIconClick = (e) => {
    e.stopPropagation();
    setShowUserMenu(!showUserMenu);
    if (onUserClick) onUserClick();
  };

  const handleSignOut = async () => {
    console.log("signing out");
    await signOut({
      returnTo: window.location.origin,
    });

    setShowUserMenu(false);
  };

  const handleSignIn = async () => {
    try {
      console.log("Starting sign in process...");
      await signIn({
        hostedAuth: true,
        returnToUrl: window.location.origin,
        prompt: "select_account",
        logout_mode: "global",
      });
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  const handleSettingsClick = () => {
    navigate("/settings");
    setShowUserMenu(false);
  };

  const handleConnectWallet = async () => {
    setIsConnectingWallet(true);
    try {
      await connectWallet();
    } catch (err) {
      console.error("Error connecting wallet:", err);
    } finally {
      setIsConnectingWallet(false);
      setShowUserMenu(false);
    }
  };

  const handleDisconnectWallet = () => {
    disconnectWallet();
    setShowUserMenu(false);
  };

  const isDarkHeader = brand?.headerStyle === "dark";

  return (
    <header
      className={`h-16 flex justify-between items-center px-6 md:px-8 fixed top-0 z-40 transition-all duration-200 ease-out w-full ${
        isHistoryOpen ? "md:left-[400px] md:w-[calc(100%-400px)]" : "left-0"
      }`}
    >
      {/* Brand-aware header background */}
      <div className={`absolute inset-0 z-0 ${isDarkHeader ? "bg-penta-black" : "bg-penta-grey-light"}`} />

      <div className="relative z-10 flex w-full justify-between items-center">
        <div className="flex items-center gap-4">
          {Boolean(user) && (
            <button
              className={`cursor-pointer transition-opacity duration-200 ease-out p-2 select-none w-[36px] h-[36px] flex items-center justify-center hover:opacity-70 ${isDarkHeader ? "text-penta-white" : ""}`}
              onClick={onPanelClick}
              aria-label={isHistoryOpen ? "Close panel" : "Open panel"}
            >
              {isHistoryOpen ? (
                <div className="w-5 h-5" />
              ) : (
                <PanelRightClose
                  className={`cursor-pointer ${isDarkHeader ? "text-penta-white" : "text-penta-black"}`}
                  size={20}
                  strokeWidth={1}
                />
              )}
            </button>
          )}
          <Link to="/" className="flex items-center">
            <img
              src={brand?.branding?.logoUrl || "/brands/pentatonic/logo.svg"}
              alt={brand?.branding?.logoAlt || brand?.name || "Home"}
              className={`h-5 md:h-6 ${isDarkHeader ? "brightness-0 invert" : ""}`}
            />
          </Link>
        </div>

        <div className="flex justify-end">
          {user ? (
            <Dropdown
              open={showUserMenu}
              onOpenChange={setShowUserMenu}
              trigger={
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                  className={`cursor-pointer transition-all duration-200 ease-out p-2 select-none ${
                    showUserMenu
                      ? isDarkHeader ? "bg-penta-white text-penta-black" : "bg-penta-black text-penta-white"
                      : isDarkHeader ? "text-penta-white hover:opacity-70" : "text-penta-black hover:opacity-70"
                  }`}
                  onClick={handleUserIconClick}
                >
                  <User className="cursor-pointer" size={20} strokeWidth={1} />
                </div>
              }
            >
              {/* Wallet Connection Status */}
              {walletConnected ? (
                <>
                  <div className="px-3 py-2 text-xs text-penta-grey-medium border-b border-penta-grey-light">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet size={12} className="text-green-500" />
                        <span>Wallet Connected</span>
                      </div>
                      <ConsentLevelIndicator />
                    </div>
                    {walletProfile?.email && (
                      <div className="text-penta-black mt-1 truncate">
                        {walletProfile.email}
                      </div>
                    )}
                    <ConsentEscalationButton className="mt-2" />
                  </div>
                  <Dropdown.Item onSelect={handleDisconnectWallet}>
                    <Unlink size={16} className="mr-2" />
                    Disconnect Wallet
                  </Dropdown.Item>
                </>
              ) : (
                <Dropdown.Item onSelect={handleConnectWallet} disabled={isConnectingWallet}>
                  <LinkIcon size={16} className="mr-2" />
                  {isConnectingWallet ? "Connecting..." : "Connect Wallet"}
                </Dropdown.Item>
              )}
              <Dropdown.Item onSelect={handleSettingsClick}>
                <Settings size={16} className="mr-2" />
                Settings
              </Dropdown.Item>
              <Dropdown.Item onSelect={handleSignOut}>
                <LogOut size={16} className="mr-2" />
                Sign Out
              </Dropdown.Item>
            </Dropdown>
          ) : (
            <>
              {!isLoading && (
                <Button onClick={handleSignIn} size="sm">
                  Sign In
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
